import { inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpHeaders,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { CapacitorHttp } from '@capacitor/core';
import { from, Observable } from 'rxjs';
import { LoggingService } from '../services/logging.service';

const TAG = '[NativeHTTP]';

function isNativePlatform(): boolean {
  const capacitor = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  return capacitor?.isNativePlatform?.() ?? false;
}

export const nativeHttpInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const loggingService = inject(LoggingService);
  if (!isNativePlatform()) {
    return next(req);
  }

  if (shouldSkipNativeHttp(req.url)) {
    return next(req);
  }

  if (req.body instanceof FormData) {
    return next(req);
  }

  return from(executeNativeRequest(req, loggingService));
};

const SKIP_NATIVE_PATTERNS = [
  'servisbot',
  'servismatrix',
  'helium',
  'b2clogin.com',
  'microsoftonline.com',
  'login.microsoft',
  'localhost',
  '10.0.2.2',
];

function shouldSkipNativeHttp(url: string): boolean {
  if (!url.startsWith('http')) {
    return true;
  }
  return SKIP_NATIVE_PATTERNS.some((pattern) => url.includes(pattern));
}

function toHttpHeaders(capHeaders: Record<string, string> | undefined): HttpHeaders {
  if (!capHeaders) {
    return new HttpHeaders();
  }
  return new HttpHeaders(capHeaders);
}

async function executeNativeRequest(req: HttpRequest<unknown>, loggingService: LoggingService): Promise<HttpResponse<unknown>> {
  const headers: Record<string, string> = {};
  for (const key of req.headers.keys()) {
    const value = req.headers.get(key);
    if (value) {
      headers[key] = value;
    }
  }

  if (req.body !== null && req.body !== undefined && !headers['Content-Type'] && !headers['content-type']) {
    if (typeof req.body === 'object') {
      headers['Content-Type'] = 'application/json';
    } else if (typeof req.body === 'string') {
      headers['Content-Type'] = 'text/plain';
    }
  }

  let responseType: 'json' | 'text' | 'blob' | 'arraybuffer' = 'json';
  if (req.responseType === 'text') {
    responseType = 'text';
  } else if (req.responseType === 'blob') {
    responseType = 'blob';
  } else if (req.responseType === 'arraybuffer') {
    responseType = 'arraybuffer';
  }

  let data: unknown = undefined;
  if (req.body !== null && req.body !== undefined) {
    if (
      typeof req.body === 'object' &&
      !(req.body instanceof FormData) &&
      !(req.body instanceof Blob) &&
      !(req.body instanceof ArrayBuffer)
    ) {
      // Pre-stringify JSON bodies using JavaScript's JSON.stringify to ensure
      // consistent floating-point number serialization across platforms.
      // Without this, iOS's NSJSONSerialization can produce extra precision
      // (e.g. 2700.08 → 2700.0800000000001) causing backend validation
      // mismatches such as TotalDraftAmount errors.
      data = JSON.stringify(req.body);
    } else {
      data = req.body;
    }
  }

  try {
    const response = await CapacitorHttp.request({
      url: req.urlWithParams,
      method: req.method,
      headers,
      data,
      responseType,
    });

    if (response.status >= 200 && response.status < 300) {
      let body: unknown = response.data;

      if (req.responseType === 'blob' && typeof body === 'string') {
        const contentType = response.headers?.['content-type'] || 'application/octet-stream';
        body = base64ToBlob(body, contentType);
      }

      if (req.responseType === 'text' && typeof body !== 'string') {
        body = body != null ? JSON.stringify(body) : '';
      }

      return new HttpResponse({
        body,
        headers: toHttpHeaders(response.headers),
        status: response.status,
        statusText: getStatusText(response.status),
        url: response.url || req.urlWithParams,
      });
    }

    throw new HttpErrorResponse({
      error: response.data,
      headers: toHttpHeaders(response.headers),
      status: response.status,
      statusText: getStatusText(response.status),
      url: response.url || req.urlWithParams,
    });
  } catch (error) {
    if (error instanceof HttpErrorResponse) {
      throw error;
    }

    loggingService.logException(error, `${TAG} ✕ ${req.method} ${req.urlWithParams.substring(0, 120)}`);
    throw new HttpErrorResponse({
      error: error,
      status: 0,
      statusText: 'Native HTTP Error',
      url: req.urlWithParams,
    });
  }
}

function getStatusText(status: number): string {
  const statusTexts: Record<number, string> = {
    200: 'OK',
    201: 'Created',
    204: 'No Content',
    301: 'Moved Permanently',
    302: 'Found',
    304: 'Not Modified',
    400: 'Bad Request',
    401: 'Unauthorized',
    403: 'Forbidden',
    404: 'Not Found',
    500: 'Internal Server Error',
    502: 'Bad Gateway',
    503: 'Service Unavailable',
  };
  return statusTexts[status] || '';
}

function base64ToBlob(base64: string, contentType: string): Blob {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: contentType });
}
